/**
 * Onboarding navigation driven by the server.
 *
 * The backend owns the step order: each vertical module supplies the plan for
 * the tenant's operating model, and `complete-step` answers with the step to
 * show next. Pages navigate to that answer rather than to a hardcoded path, so
 * the page flow and the order the backend enforces cannot drift apart.
 */
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@lipaflow/shared'

export interface OnboardingPlanStep {
  id: string
  label: string
  description: string
}

/** What `POST /onboarding/progress/complete-step` returns. */
export interface StepCompletion {
  next_step: string | null
}

/** The page for a step; the dashboard once there is no next step. */
export function onboardingStepPath(step: string | null | undefined): string {
  return step ? `/onboarding/step/${step}` : '/'
}

export async function completeOnboardingStep(step: string): Promise<StepCompletion> {
  const res = await api.post<StepCompletion>('/onboarding/progress/complete-step', { step })
  return res.data
}

/**
 * Steps in the tenant's plan, with go-live last. Shares the layout's config
 * query. `isReady` is false until the plan has loaded.
 */
export function useOnboardingPlan(): { plan: string[]; isReady: boolean } {
  const { data: config } = useQuery<{ steps: OnboardingPlanStep[] }>({
    queryKey: ['onboarding-config'],
    queryFn: async () => (await api.get('/onboarding/config')).data,
  })
  const ids = (config?.steps ?? []).map((step) => step.id)
  return {
    plan: ids.includes('go_live') ? ids : [...ids, 'go_live'],
    isReady: Boolean(config),
  }
}

export function useOnboardingNavigation(currentStep: string) {
  const navigate = useNavigate()
  const { plan } = useOnboardingPlan()
  const index = plan.indexOf(currentStep)
  const previousStep = index > 0 ? plan[index - 1] : null

  const goTo = useCallback(
    (step: string | null | undefined) => navigate(onboardingStepPath(step)),
    [navigate],
  )
  const goBack = useCallback(
    () => navigate(onboardingStepPath(previousStep ?? plan[0])),
    [navigate, previousStep, plan],
  )

  return { goTo, goBack, previousStep }
}

/**
 * "Step 2 of 4": where a step sits in this tenant's plan. Going live is not
 * counted, it is where the plan ends. The plan's length depends on the
 * tenant (a CBC-only school has no bank step), so no page hardcodes it.
 */
export function useStepPosition(step: string): string {
  const { plan } = useOnboardingPlan()
  const own = plan.filter((id) => id !== 'go_live')
  const index = own.indexOf(step)
  return index < 0 ? '' : `Step ${index + 1} of ${own.length}.`
}

/**
 * The step to open when someone comes back to setup: the first in their plan
 * they have not completed. It used to be the first step every time, so a
 * returning customer was asked for details they had already saved.
 */
export function firstOpenStep(plan: string[], completed: Record<string, unknown> | null | undefined): string {
  return plan.find((step) => !completed?.[step]) ?? plan[plan.length - 1] ?? 'set_institution_details'
}
